import { DirectiveBinding } from "vue";
import { get, isEmpty, isNil } from "lodash";

const allowedKeys = [
  "Backspace", "Delete", "Tab", "Escape", "Enter",
  "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown",
];

enum FormatterTypes {
  INTEGER = "integer",
  DECIMAL = "decimal",
  STRING_TRANSFORM = "string_transform",
}

function integerFormatter(el: HTMLInputElement, config: Record<string, any>) {
  const thousandSeparator = get(config, "thousandSeparator", "");
  const value = thousandSeparator ? el.value.replaceAll(thousandSeparator, "") : el.value;

  el.value = value.replaceAll(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
}

function integerEventListeners(el: HTMLInputElement, config: Record<string, any>) {
  const thousandSeparator = get(config, "thousandSeparator", "");

  el.addEventListener("keydown", (event: KeyboardEvent) => {
    const key = event.key;

    if (
      (event.ctrlKey || event.metaKey)
      && ["a", "c", "x", "v"].includes(event.key.toLowerCase())
    ) {
      return;
    }

    if (allowedKeys.includes(key) || /^\d$/.test(key)) {
      return;
    }

    event.preventDefault();
  });

  el.addEventListener("paste", (event: ClipboardEvent) => {
    const value = event.clipboardData?.getData("text").replaceAll(thousandSeparator, "") || "";

    if (!isEmpty(value) && !/^\d+$/.test(value)) {
      event.preventDefault();
    }
  });
}

function decimalFormatter(el: HTMLInputElement, config: Record<string, any>) {
  const { decimalSeparator, thousandSeparator } = config;

  const escapedSeparator = decimalSeparator.replaceAll(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");

  const selectionStart = get(el, "selectionStart", 0) as number;

  const digitsBeforeCursor = (
    el.value.slice(0, selectionStart)
      .match(new RegExp(`\\d|${escapedSeparator}`, "g")) || []
  )
    .length;

  const value = thousandSeparator ? el.value.replaceAll(thousandSeparator, "") : el.value;

  const [integerPart, decimalPart] = value.split(decimalSeparator);

  const formattedIntegerPart = integerPart.replaceAll(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);

  const formatted = isNil(decimalPart)
    ? formattedIntegerPart
    : `${formattedIntegerPart}${decimalSeparator}${decimalPart}`;

  el.value = formatted;

  let digits = 0;
  let position = 0;

  for (let i = 0; i < formatted.length; i++) {
    if (digitsBeforeCursor === 0) {
      break;
    }

    if (new RegExp(`\\d|${escapedSeparator}`).test(formatted[i])) {
      digits++;
    }

    if (digits >= digitsBeforeCursor) {
      position = i + 1;
      break;
    }
  }

  el.setSelectionRange(position, position);
}

function decimalEventListeners(el: HTMLInputElement, config: Record<string, any>) {
  const { decimalSeparator, thousandSeparator, precision } = config;

  el.addEventListener("keydown", (event: KeyboardEvent) => {
    const key = event.key;

    if (
      (event.ctrlKey || event.metaKey)
      && ["a", "c", "x", "v"].includes(event.key.toLowerCase())
    ) {
      return;
    }

    if (key === decimalSeparator) {
      if (el.value.includes(decimalSeparator)) {
        event.preventDefault();
      }

      return;
    }

    if ([...allowedKeys, decimalSeparator].includes(key)) {
      return;
    }

    if (/(^\d$)/.test(key)) {
      const decimalSeparatorPosition = el.value.indexOf(decimalSeparator);

      if (
        decimalSeparatorPosition >= 0
        && get(el, "selectionStart", 0) as number > decimalSeparatorPosition
      ) {
        const [, decimalPart] = el.value.split(decimalSeparator);

        if (decimalPart.length >= precision) {
          event.preventDefault();
        }
      }

      return;
    }

    event.preventDefault();
  });

  el.addEventListener("paste", (event: ClipboardEvent) => {
    const value = event.clipboardData?.getData("text").replaceAll(thousandSeparator, "") || "";
    const escapedSeparator = decimalSeparator.replaceAll(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
    let regex = new RegExp(`^(0|[1-9][0-9]*)(${escapedSeparator}[0-9]+)?$`);

    if (!isEmpty(value) && !regex.test(value)) {
      event.preventDefault();
    }
  });
}

function stringTransformFormatter(el: HTMLInputElement, config: Record<string, any>) {
  const transform = get(config, "transform", "default");

  switch (transform) {
    case "uppercase":
      el.value = el.value.toUpperCase();
      break;

    case "lowercase":
      el.value = el.value.toLowerCase();
      break;

    case "capitalize":
      el.value = el.value.replace(
        /(\p{L})(\p{L}*)/gu,
        (_, firstChar, remainder) => firstChar.toUpperCase() + remainder.toLowerCase(),
      );
      break;

    default:
      break;
  }
}

export default {
  created(el: HTMLInputElement, binding: DirectiveBinding) {
    const type = get(binding, "value.type", "default");
    const options = get(binding, "value.options", {});

    const handlers: Record<string, Function> = {
      [FormatterTypes.INTEGER]: integerEventListeners,
      [FormatterTypes.DECIMAL]: decimalEventListeners,
    };

    handlers[type]?.(el, options);
  },

  updated(el: HTMLInputElement, binding: DirectiveBinding) {
    const type = get(binding, "value.type", "default");
    const options = get(binding, "value.options", {});

    const handlers: Record<string, Function> = {
      [FormatterTypes.INTEGER]: integerFormatter,
      [FormatterTypes.DECIMAL]: decimalFormatter,
      [FormatterTypes.STRING_TRANSFORM]: stringTransformFormatter,
    };

    handlers[type]?.(el, options);

    el.dispatchEvent(new Event("input"));
  },
};